import { c as _c } from "react/compiler-runtime";
import { useMapSearchResults } from "@catchy/app-core/src/useMapSearchResults";
import { getMapCenterDefault } from "@/lib/regions/client";
import { rankMapSearchSuggestions, matchesStopSearch, getMapSuggestionText as suggestionText } from "@catchy/app-core/src/stopSearchMatching";
import { IconButton, SearchResultIcon } from "@catchy/ui";
import { useEffect, useMemo, useRef, useState } from "react";
import useSWR from "swr";
import { Combobox, ComboboxInput, ComboboxOption, ComboboxOptions } from "@headlessui/react";
import SearchIcon from "@catchy/assets/svgs/search.svg";
import CloseCross from "@catchy/assets/svgs/close-cross.svg";
import Location from "@catchy/assets/svgs/location-outline.svg";
import StopMarker from "@catchy/assets/svgs/stop-mono-lg.svg";
import { buildRecentStopSummaryUrl, getPreferredStopName, getPublicStopIdentifier, getStoredStopId, fetchJsonArray, getStopSearchResourceState, normalizeStopDescription, useDebouncedValue } from "@catchy/app-core";
import { getEffectiveStopType, getStopTypeColorClass } from "@/lib/stopTypes";
import { getStopTypeDetails } from "@/lib/transitDisplay";
const isValidStopSuggestion = suggestion => suggestion?.type === "stop" && typeof suggestion.stop_id === "string" && suggestion.stop_id.length > 0 && typeof suggestion.stop_name === "string" && suggestion.stop_name.length > 0;
const isValidLocationSuggestion = suggestion => suggestion?.type === "Feature" && Array.isArray(suggestion.geometry?.coordinates) && suggestion.geometry.coordinates.length >= 2 && typeof suggestion.place_name === "string";
const Search = t0 => {
  const $ = _c(73);
  const {
    className: t1,
    variant: t2,
    onActiveChange,
    onSelectLocation,
    onSelectStop,
    lastMapCenter,
    preferredStopNames
  } = t0;
  const className = t1 === undefined ? "" : t1;
  const variant = t2 === undefined ? "overlay" : t2;
  const [query, setQuery] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef();
  const blurFrameRef = useRef(null);
  const previousSearchTermRef = useRef("");
  const searchQuery = typeof query === "string" ? query : "";
  let t3;
  if ($[0] !== searchQuery) {
    t3 = searchQuery.trim();
    $[0] = searchQuery;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const searchTerm = t3;
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);
  const [searchOrigin, setSearchOrigin] = useState(null);
  let t4;
  if ($[2] !== debouncedSearchTerm || $[3] !== lastMapCenter?.center?.lat || $[4] !== lastMapCenter?.center?.lng) {
    t4 = () => {
      if (debouncedSearchTerm.length < 2) {
        previousSearchTermRef.current = "";
        setSearchOrigin(null);
        return;
      }
      if (debouncedSearchTerm === previousSearchTermRef.current) {
        return;
      }
      previousSearchTermRef.current = debouncedSearchTerm;
      setSearchOrigin({
        lat: lastMapCenter?.center?.lat ?? getMapCenterDefault().center.lat,
        lng: lastMapCenter?.center?.lng ?? getMapCenterDefault().center.lng
      });
    };
    $[2] = debouncedSearchTerm;
    $[3] = lastMapCenter?.center?.lat;
    $[4] = lastMapCenter?.center?.lng;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = lastMapCenter?.center?.lat;
  const t6 = lastMapCenter?.center?.lng;
  let t7;
  if ($[6] !== debouncedSearchTerm || $[7] !== t5 || $[8] !== t6) {
    t7 = [debouncedSearchTerm, t5, t6];
    $[6] = debouncedSearchTerm;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  useEffect(t4, t7);
  let t8;
  if ($[10] !== searchOrigin || $[11] !== searchTerm) {
    t8 = searchTerm.length >= 2 && searchOrigin ? `/api/mapsearch?query=${encodeURIComponent(searchTerm)}&centre_lat=${Number(searchOrigin.lat).toFixed(3)}&centre_lng=${Number(searchOrigin.lng).toFixed(3)}` : null;
    $[10] = searchOrigin;
    $[11] = searchTerm;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const searchKey = t8;
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      if (blurFrameRef.current !== null) {
        window.cancelAnimationFrame(blurFrameRef.current);
        blurFrameRef.current = null;
      }
      setIsFocused(true);
    };
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const activateSearch = t9;
  let t10;
  if ($[14] !== onActiveChange) {
    t10 = () => {
      setIsFocused(false);
      onActiveChange?.(false);
      if (blurFrameRef.current !== null) {
        window.cancelAnimationFrame(blurFrameRef.current);
      }
      blurFrameRef.current = window.requestAnimationFrame(() => {
        inputRef.current?.blur();
        blurFrameRef.current = null;
      });
    };
    $[14] = onActiveChange;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const dismissSearchResults = t10;
  const {
    data,
    error,
    isLoading,
    isValidating,
    mutate
  } = useMapSearchResults(searchKey, debouncedSearchTerm === searchTerm);
  const t11 = searchTerm.length >= 2;
  let t12;
  if ($[16] !== data || $[17] !== error || $[18] !== isLoading || $[19] !== isValidating || $[20] !== t11) {
    t12 = getStopSearchResourceState({
      data,
      error,
      isLoading,
      isValidating,
      isPending: t11
    });
    $[16] = data;
    $[17] = error;
    $[18] = isLoading;
    $[19] = isValidating;
    $[20] = t11;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const searchResourceState = t12;
  let t13;
  bb0: {
    if (!searchTerm) {
      let t14;
      if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
        t14 = [];
        $[22] = t14;
      } else {
        t14 = $[22];
      }
      t13 = t14;
      break bb0;
    }
    let t14;
    if ($[23] !== preferredStopNames || $[24] !== searchTerm) {
      let t15;
      if ($[26] !== searchTerm) {
        t15 = stop => typeof getStoredStopId(stop) === "string" && getStoredStopId(stop).length > 0 && typeof stop.name === "string" && stop.name.length > 0 && matchesStopSearch({
          preferred_name: stop.name
        }, searchTerm);
        $[26] = searchTerm;
        $[27] = t15;
      } else {
        t15 = $[27];
      }
      t14 = (preferredStopNames || []).filter(t15).slice(0, 10);
      $[23] = preferredStopNames;
      $[24] = searchTerm;
      $[25] = t14;
    } else {
      t14 = $[25];
    }
    t13 = t14;
  }
  const matchedPreferredStops = t13;
  let t14;
  if ($[28] !== matchedPreferredStops) {
    t14 = matchedPreferredStops.map(_temp);
    $[28] = matchedPreferredStops;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  const matchedPreferredStopIds = t14;
  let t15;
  if ($[30] !== matchedPreferredStopIds) {
    t15 = buildRecentStopSummaryUrl(matchedPreferredStopIds, process.env.NEXT_PUBLIC_APP_VERSION);
    $[30] = matchedPreferredStopIds;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  const renamedStopKey = t15;
  let t16;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      shouldRetryOnError: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
      revalidateOnMount: true,
      dedupingInterval: 60000
    };
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  const {
    data: renamedStopData
  } = useSWR(renamedStopKey, fetchJsonArray, t16);
  let t17;
  if ($[33] !== data || $[34] !== preferredStopNames || $[35] !== renamedStopData || $[36] !== searchTerm) {
    const stopSuggestions = [...(renamedStopData || []).map(_temp2), ...(Array.isArray(data) ? data : [])];
    const seenStopIds = new Set();
    const results_0 = stopSuggestions.reduce((results, item_0) => {
      if (item_0.type === "stop") {
        const stopSuggestion = {
          ...item_0,
          preferred_name: getPreferredStopName(preferredStopNames, item_0.stop_id, item_0.stop_code),
          type: "stop"
        };
        if (!isValidStopSuggestion(stopSuggestion)) {
          return results;
        }
        if (seenStopIds.has(item_0.stop_id)) {
          return results;
        }
        seenStopIds.add(item_0.stop_id);
        results.push(stopSuggestion);
        return results;
      }
      if (isValidLocationSuggestion(item_0)) {
        results.push(item_0);
      }
      return results;
    }, []);
    t17 = rankMapSearchSuggestions(results_0, searchTerm);
    $[33] = data;
    $[34] = preferredStopNames;
    $[35] = renamedStopData;
    $[36] = searchTerm;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  const suggestions = t17;
  const isSearching = isFocused && searchTerm.length > 0;
  let t18;
  let t19;
  if ($[38] !== isSearching || $[39] !== onActiveChange) {
    t18 = () => {
      onActiveChange?.(isSearching);
    };
    t19 = [isSearching, onActiveChange];
    $[38] = isSearching;
    $[39] = onActiveChange;
    $[40] = t18;
    $[41] = t19;
  } else {
    t18 = $[40];
    t19 = $[41];
  }
  useEffect(t18, t19);
  const wrapperClassName = variant === "sidebar" ? "relative flex h-full min-h-0 w-full flex-col pt-2" : "relative w-full px-[20px]";
  const comboboxClassName = variant === "sidebar" ? "relative flex min-h-0 flex-1 flex-col text-lg" : "relative mb-2 pt-2 text-lg";
  const optionsClassName = variant === "sidebar" ? "relative z-20 min-h-0 flex-1 w-full max-w-full overflow-y-auto pb-4" : "absolute z-20 mt-1 max-h-[calc(var(--vvh)-8.75rem)] w-full max-w-full overflow-y-auto rounded-lg bg-white shadow-lg ring-1 ring-zinc-100 touch-pan-y dark:bg-black dark:shadow-none dark:ring-zinc-500";
  const inputWrapperClassName = variant === "sidebar" ? "group flex items-center rounded-lg bg-white ring-1 ring-zinc-500/50 transition-all duration-100 focus-within:ring focus-within:ring-blue-500/50 dark:bg-black dark:shadow-none dark:ring-zinc-500" : "group flex items-center rounded-lg bg-white ring-1 ring-zinc-200 transition-all shadow-md focus-within:shadow-lg dark:bg-black dark:shadow-none dark:ring-zinc-500";
  const t20 = `${wrapperClassName} ${className}`;
  let t21;
  if ($[42] !== t20) {
    t21 = t20.trim();
    $[42] = t20;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== dismissSearchResults || $[45] !== onSelectLocation || $[46] !== onSelectStop) {
    t22 = suggestion => {
      suggestion?.type === "stop" && (onSelectStop?.(suggestion), dismissSearchResults());
      suggestion?.type === "Feature" && (onSelectLocation?.({
        lnglat: suggestion.geometry.coordinates,
        name: suggestionText(suggestion)
      }), dismissSearchResults());
    };
    $[44] = dismissSearchResults;
    $[45] = onSelectLocation;
    $[46] = onSelectStop;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <div className="grid w-14 shrink-0 place-items-center"><SearchIcon className="left-[calc(20px+0.75rem)] top-[1.375rem] z-10 size-6 text-zinc-500 dark:text-zinc-300" title="Search icon" /></div>;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  let t24;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <ComboboxInput onChange={event => {
      activateSearch();
      setQuery(event.target.value);
    }} onPaste={() => {
      window.requestAnimationFrame(() => {
        activateSearch();
        setQuery(inputRef.current?.value || "");
      });
    }} onFocus={() => {
      activateSearch();
    }} onBlur={() => {
      window.requestAnimationFrame(() => {
        setIsFocused(false);
      });
    }} className="w-full appearance-none bg-transparent py-3 placeholder-zinc-400 outline-none" ref={inputRef} placeholder="Search" autoCorrect="off" autoComplete="off" autoCapitalize="off" spellCheck="false" enterKeyHint="go" aria-label="Search for stops or by address" />;
    $[49] = t24;
  } else {
    t24 = $[49];
  }
  let t25;
  if ($[50] !== searchQuery.length) {
    t25 = searchQuery.length > 0 && <IconButton onMouseDown={_temp3} onClick={() => {
      setIsFocused(true);
      inputRef.current.focus();
      setQuery("");
    }} label="Clear search" variant="subtle" className="right-[calc(20px+.75rem)] top-4 z-10 mr-2"><CloseCross aria-hidden="true" className="h-[1.125rem] w-[1.125rem] text-zinc-500 dark:text-zinc-300" /></IconButton>;
    $[50] = searchQuery.length;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  let t26;
  if ($[52] !== inputWrapperClassName || $[53] !== t25) {
    t26 = <div className={inputWrapperClassName}>{t23}{t24}{t25}</div>;
    $[52] = inputWrapperClassName;
    $[53] = t25;
    $[54] = t26;
  } else {
    t26 = $[54];
  }
  const t27 = `${optionsClassName} ${isSearching ? "visible" : "hidden"}`;
  let t28;
  if ($[55] !== isSearching || $[56] !== mutate || $[57] !== searchQuery || $[58] !== searchResourceState || $[59] !== suggestions) {
    t28 = isSearching && searchResourceState === "unavailable" && suggestions.length === 0 ? <div className="relative flex items-center justify-between gap-3 px-11 py-4 leading-tight opacity-70"><span>Search is unavailable. Try again.</span><button type="button" onClick={() => mutate()} className="font-semibold text-blue-600 dark:text-blue-400">Retry</button></div> : suggestions.length === 0 && isSearching ? <div className="relative cursor-default select-none py-4 pl-14 leading-tight opacity-70">{searchResourceState === "loading" ? <>Searching…</> : <>No matches for{" "}<span className="font-semibold">{searchQuery}</span></>}</div> : isSearching && <>{searchResourceState === "refresh-error" && <div className="relative flex items-center justify-between gap-3 px-11 py-3 text-sm leading-tight opacity-70"><span>Results may be out of date.</span><button type="button" onClick={() => mutate()} className="font-semibold text-blue-600 dark:text-blue-400">Retry</button></div>}{suggestions.map((suggestion_0, key) => {
        const isStopSuggestion = isValidStopSuggestion(suggestion_0);
        const isLocationSuggestion = isValidLocationSuggestion(suggestion_0);
        const stopDescription = isStopSuggestion ? normalizeStopDescription(suggestion_0.stop_description) : null;
        const effectiveStopType = isStopSuggestion ? getEffectiveStopType(suggestion_0) : null;
        const matchedStopIdentifier = isStopSuggestion && (suggestion_0.stop_code?.includes(searchQuery) || suggestion_0.stop_id?.includes(searchQuery));
        const stopMetaLine = isStopSuggestion ? [matchedStopIdentifier ? `${getStopTypeDetails(effectiveStopType).name} ${getPublicStopIdentifier(suggestion_0)}` : null, stopDescription].filter(Boolean).join(" \xB7 ") : null;
        return <ComboboxOption key={key} value={suggestion_0} as="div" className="pl-2 hover:bg-zinc-400/10 data-[focus]:bg-zinc-400/10 data-[selected]:bg-zinc-400/10"><div className="flex flex-nowrap items-start text-pretty py-4 pr-3 leading-tight transition-all duration-200">{isLocationSuggestion ? <><SearchResultIcon><Location className="relative top-px mt-px h-[1.25rem] w-4 text-zinc-900 opacity-40 dark:text-white" /></SearchResultIcon><span>{suggestionText(suggestion_0)}</span></> : isStopSuggestion ? <SearchResultIcon><StopMarker className={`relative -left-1 top-px mt-px h-[1.125rem] w-3 flex-shrink-0 ${getStopTypeColorClass(effectiveStopType)}`} /></SearchResultIcon> : null}{isStopSuggestion && <div className="flex flex-col"><div>{suggestion_0.preferred_name || suggestion_0.stop_name}</div>{suggestion_0.preferred_name && <div className="text-sm leading-none opacity-80">{suggestion_0.stop_name}</div>}{stopMetaLine && <div className="text-sm leading-none opacity-60">{stopMetaLine}</div>}</div>}</div></ComboboxOption>;
      })}</>;
    $[55] = isSearching;
    $[56] = mutate;
    $[57] = searchQuery;
    $[58] = searchResourceState;
    $[59] = suggestions;
    $[60] = t28;
  } else {
    t28 = $[60];
  }
  let t29;
  if ($[61] !== t27 || $[62] !== t28) {
    t29 = <ComboboxOptions modal={false} className={t27}>{t28}</ComboboxOptions>;
    $[61] = t27;
    $[62] = t28;
    $[63] = t29;
  } else {
    t29 = $[63];
  }
  let t30;
  if ($[64] !== comboboxClassName || $[65] !== searchQuery || $[66] !== t22 || $[67] !== t26 || $[68] !== t29) {
    t30 = <Combobox as="div" immediate={true} value={searchQuery} onChange={t22} className={comboboxClassName}>{t26}{t29}</Combobox>;
    $[64] = comboboxClassName;
    $[65] = searchQuery;
    $[66] = t22;
    $[67] = t26;
    $[68] = t29;
    $[69] = t30;
  } else {
    t30 = $[69];
  }
  let t31;
  if ($[70] !== t21 || $[71] !== t30) {
    t31 = <div className={t21}>{t30}</div>;
    $[70] = t21;
    $[71] = t30;
    $[72] = t31;
  } else {
    t31 = $[72];
  }
  return t31;
};
export default Search;
function _temp(preferredStopName) {
  return getStoredStopId(preferredStopName);
}
function _temp2(item) {
  return {
    ...item,
    type: "stop"
  };
}
function _temp3(event_0) {
  event_0.preventDefault();
}